/**
 * voice-cards.js — the catalogue voice cards of /voice-gender, ONE builder for both lanes
 * that list catalogue voices: «Другой голос» (server, tokens) and «Голоса ИИ · в браузере»
 * (window.VoiceConvert). The person compares voices across the two by the same card: name,
 * ♀/♂, words read off the voice («низкий · звонкий»), ~F0, ▶ preview through the page's one
 * clip player (VGClips — two clips at once cannot be compared), and the filter «Все / Женские /
 * Мужские / Образы» that appears when the list is longer than a glance.
 *
 * «Язык голоса» (chat 694, 19.10: «нужна нормальная русская речь, Айгуль и Жазиры — с
 * акцентом»): every voice says which language its sample speaks (`lang`) and the speaker's
 * native language (`native`). The Silero ru_* speakers are bilingual natives of other languages,
 * so the select splits a language into «родной» and «второй язык»; the choice is remembered
 * (__toolSettings, one key for both lanes). Measured the same day (VoxLingua P(ru) of clean native
 * speech converted into all 18 voices; the samples themselves score 0.15–1.00): the words and the
 * intonation stay the person's, but some accent does come through — the server's engine 0.81–1.00
 * (Мира 0.81, Жазира 0.84), v2 0.96–1.00, the browser's RVC 0.98–1.00. The note under the select
 * says exactly that wherever a second-language voice is in view.
 *
 *   VGVoiceCards.render(box, voices, {
 *       t, tags, clips, clipPrefix,      // dictionary, tag words, VGClips, key prefix per lane
 *       name(v), showKind,               // display name; «речь/вокал» chip when both exist
 *       onPreview(v), onPick(v, card)
 *   })
 *   VGVoiceCards.filter(bar, box, voices, { t, onChange(f), onLang(lang) })   → { value(), lang(), apply() }
 *   VGVoiceCards.select(box, card)       — the radio state
 *   VGVoiceCards.matches(v, f, lang), VGVoiceCards.langOptions(voices, t), VGVoiceCards.sub(v, opts)
 */
(function () {
'use strict';

function langOf(v) { return v.lang || 'ru'; }
function isNative(v) { return (v.native || langOf(v)) === langOf(v); }

/** lang: 'all' | '<code>' (native speakers of it) | '<code>~' (it is their second language). */
function inLang(v, lang) {
    if (!lang || lang === 'all') return true;
    var second = lang.charAt(lang.length - 1) === '~';
    return langOf(v) === (second ? lang.slice(0, -1) : lang) && isNative(v) !== second;
}

function matches(v, f, lang) {
    if (!inLang(v, lang)) return false;
    if (!f || f === 'all') return true;
    if (f === 'char') return v.group === 'char';
    return v.gender === f && v.group !== 'char';
}

/** The select's options, from the catalogue itself: bigger language first, its natives first. */
function langOptions(voices, t) {
    var seen = {}, per = {}, out = [];
    voices.forEach(function (v) {
        var key = langOf(v) + (isNative(v) ? '' : '~');
        if (!(key in seen)) { seen[key] = 0; out.push(key); }
        seen[key]++;
        per[langOf(v)] = (per[langOf(v)] || 0) + 1;
    });
    out.sort(function (a, b) {
        var la = a.replace('~', ''), lb = b.replace('~', '');
        return la === lb ? (a.length - b.length) : (per[lb] - per[la]);
    });
    return out.map(function (key) {
        var code = key.replace('~', ''), name = t('ai_lang_' + code);
        if (!name || name === 'ai_lang_' + code) name = code.toUpperCase();
        return { value: key, n: seen[key], label: t(key === code ? 'ai_lang_native' : 'ai_lang_second').replace(':lang', name) };
    });
}

/** «низкий · спокойный · ~110 Гц»: words measured when the voice was built (config tags). */
function sub(v, o) {
    var parts = [], tags = o.tags || {};
    if (o.showKind) parts.push(v.kind === 'vocal' ? o.t('ai_kind_vocal') : o.t('ai_kind_speech'));
    (v.tags || []).forEach(function (k) { if (tags[k]) parts.push(tags[k]); });
    if (v.f0) parts.push('~' + Math.round(v.f0) + ' ' + o.t('unit_hz'));
    return parts.join(' · ');
}

function select(box, card) {
    box.querySelectorAll('.vg-ai-voice').forEach(function (x) {
        x.classList.toggle('active', x === card);
        x.setAttribute('aria-checked', x === card ? 'true' : 'false');
    });
}

function render(box, voices, o) {
    voices.forEach(function (v) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'vg-ai-voice'; b.setAttribute('role', 'radio'); b.setAttribute('aria-checked', 'false');
        b.setAttribute('data-vid', v.id);
        var f = v.gender === 'f';
        b.innerHTML = '<span class="vg-ai-voice-top"><span class="vg-ai-voice-g ' + (f ? 'f' : 'm') + '">' + (f ? '♀' : '♂') + '</span>'
            + '<span class="vg-ai-voice-name"></span>'
            + (v.preview ? '<span class="vg-ai-voice-play" title="' + o.t('play') + '">▶</span>' : '')
            + '</span><span class="vg-ai-voice-sub"></span>';
        b.querySelector('.vg-ai-voice-name').textContent = o.name(v);
        b.querySelector('.vg-ai-voice-sub').textContent = sub(v, o);
        var pv = b.querySelector('.vg-ai-voice-play'), key = (o.clipPrefix || 'voice:') + v.id;
        if (pv && o.clips) {
            o.clips.register(key, function (phase) {
                pv.classList.toggle('loading', phase === 'loading');
                pv.textContent = phase === 'playing' ? '❚❚' : (phase === 'loading' ? '' : '▶');
            });
        }
        b.addEventListener('click', function (e) {
            if (e.target.closest('.vg-ai-voice-play')) {
                e.preventDefault();
                if (o.clips && o.clips.toggle(key, v.preview) && o.onPreview) o.onPreview(v);
                return;
            }
            select(box, b);
            if (o.onPick) o.onPick(v, b);
        });
        box.appendChild(b);
    });
}

function filter(bar, box, voices, o) {
    var state = 'all', lang = 'all', note = null;
    var langs = langOptions(voices, o.t);
    var byId = {};
    voices.forEach(function (v) { byId[v.id] = v; });
    var count = function (f) { return voices.filter(function (v) { return matches(v, f, lang); }).length; };
    var apply = function () {
        var second = false;
        // a chip with nothing behind it in this language is not offered; if it was the chosen
        // one, the person lands on «Все», not on an empty list
        if (state !== 'all' && count(state) === 0) {
            state = 'all';
            bar.querySelectorAll('.tc-chip[data-f]').forEach(function (x) {
                var on = x.getAttribute('data-f') === 'all'; x.classList.toggle('on', on); x.setAttribute('aria-pressed', on ? 'true' : 'false');
            });
        }
        box.querySelectorAll('.vg-ai-voice[data-vid]').forEach(function (b) {
            var v = byId[b.getAttribute('data-vid')];
            // the chosen card never disappears from under the person
            b.hidden = !!v && !matches(v, state, lang) && !b.classList.contains('active');
            if (v && !b.hidden && !isNative(v)) second = true;
        });
        bar.querySelectorAll('.tc-chip[data-f]').forEach(function (c) {
            var f = c.getAttribute('data-f'), n = count(f);
            c.textContent = c.getAttribute('data-label') + ' · ' + n;
            c.hidden = f !== 'all' && n === 0;
        });
        if (note) note.hidden = !second;
    };
    var api = { value: function () { return state; }, lang: function () { return lang; }, apply: apply };
    bar.innerHTML = '';
    if (voices.length <= 6) { bar.hidden = true; return api; }

    if (langs.length > 1) {
        var saved = window.__toolSettings ? window.__toolSettings.load('voice-gender', 'voice_lang', 'all') : 'all';
        if (langs.some(function (l) { return l.value === saved; })) lang = saved;
        var sel = document.createElement('select');
        sel.className = 'tc-in vg-ai-lang';
        sel.setAttribute('aria-label', o.t('ai_lang_label'));
        [{ value: 'all', label: o.t('ai_lang_all'), n: voices.length }].concat(langs).forEach(function (l) {
            var op = document.createElement('option');
            op.value = l.value; op.textContent = l.label + ' · ' + l.n;
            sel.appendChild(op);
        });
        sel.value = lang;
        sel.addEventListener('change', function () {
            lang = sel.value;
            if (window.__toolSettings) window.__toolSettings.save('voice-gender', 'voice_lang', lang);
            apply();
            if (o.onLang) o.onLang(lang);
        });
        bar.appendChild(sel);
    }
    var opts = [['all', o.t('ai_filter_all')], ['f', o.t('ai_filter_f')], ['m', o.t('ai_filter_m')]];
    if (voices.some(function (v) { return v.group === 'char'; })) opts.push(['char', o.t('ai_filter_char')]);
    opts.forEach(function (op) {
        var c = document.createElement('button');
        c.type = 'button'; c.className = 'tc-chip' + (op[0] === state ? ' on' : '');
        c.setAttribute('aria-pressed', op[0] === state ? 'true' : 'false');
        c.setAttribute('data-f', op[0]); c.setAttribute('data-label', op[1]);
        c.addEventListener('click', function () {
            state = op[0];
            bar.querySelectorAll('.tc-chip').forEach(function (x) { var on = x === c; x.classList.toggle('on', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
            apply();
            if (o.onChange) o.onChange(state);
        });
        bar.appendChild(c);
    });
    if (langs.length > 1 && voices.some(function (v) { return !isNative(v); })) {
        note = document.createElement('p');
        note.className = 'tc-hint vg-ai-lang-note';
        note.textContent = o.t('ai_lang_note');
        bar.appendChild(note);
    }
    bar.hidden = false;
    apply();
    return api;
}

window.VGVoiceCards = { render: render, filter: filter, select: select, matches: matches, langOptions: langOptions, isNative: isNative, sub: sub };
})();
